import { customTooltip, labelCallback, tooltipCallback, xScalesCallback, yScalesCallback } from "./utilities";
import { TranslateService } from "@ngx-translate/core";
import { Scale, Tooltip } from "chart.js";
import { NodePerformance, Product } from "@/app/model";
import { EventAPI } from "@/app/model/interface/event";
import { EventNodeAPI } from "@/app/model/interface/event-node";

(Tooltip.positioners as any).cursor = function(elements: any, eventPosition: any) {
  // Check if there is an active data point found
  if (elements && elements.length) {
    const activePoint = elements[0].element;
    
    return {
      x: activePoint.x,  // LOCK Use the exact x  of the data point
      y: eventPosition.y // FREE Use the exact y of the mouse cursor
    };
  }

  // Fallback: If no point is active, follow the mouse
  return eventPosition;
};

const getMinSeriesDistance = (eventPerformance: NodePerformance[]): number | null => {
  if (!eventPerformance?.length) {
    return null;
  }

  let minDiff = Number.POSITIVE_INFINITY;

  eventPerformance.forEach((performance) => {
    const values = [performance.metered, performance.target, performance.metered]
      .filter((value) => typeof value === "number" && Number.isFinite(value)) as number[];

    if (values.length < 2) {
      return;
    }

    for (let i = 0; i < values.length; i += 1) {
      for (let j = i + 1; j < values.length; j += 1) {
        const diff = Math.abs(values[i] - values[j]);
        if (diff > 0 && diff < minDiff) {
          minDiff = diff;
        }
      }
    }
  });

  return Number.isFinite(minDiff) ? minDiff : null;
};

export const chartOptions = (
    product: Product,
    event: EventAPI,
    eventNode: EventNodeAPI,
    locale: string,
    eventPerformance: NodePerformance[],
    granularity: number,
    translate: TranslateService,
    chartStartTime?: number,
    chartEndTime?: number | null,
    hideTimezone = false
  ) => {
    const unitOfMeasure = (product?.prez_conf?.prefered_prez_demand_uom ?? eventNode?.last_current_performance_uom)?.toLowerCase();
    const isKw = unitOfMeasure === "kw";
    const minSeriesDistance = getMinSeriesDistance(eventPerformance);
    const yTickStepSize = isKw && minSeriesDistance !== null && minSeriesDistance >= 1 && minSeriesDistance <= 10
      ? 1
      : undefined;

    return ({
      type: 'line',
      options: {
        maintainAspectRatio: false,
        spanGaps: true,
        responsive: true,
        resizeDelay: 50,
        interaction: { intersect: false, axis: 'x' },
        plugins: {
          legend: {
            display: false,
          },
          yTitleTop: {
            text: product.prez_conf.prefered_prez_demand_uom ?? eventNode.last_current_performance_uom,
            display: true,
          },
          tooltip: {
            enabled: false,
            position: 'cursor',
            intersect: false, 
            mode: 'nearest',
            axis: 'x',
            callbacks: {
              title: (context: any) => tooltipCallback(context, event, locale),
              label: (context: any) => labelCallback(context, product, locale, eventNode, eventPerformance),
            },
            external: function (context: any) {
              return customTooltip(context, translate) 
            } 
          }
        },
        scales: {
          x: {
            type: "time",
            time: {
              unit: 'minute',
              displayFormats: {
                minute: 'HH:mm'
              },
            },
            ticks: {
              autoSkip: false,
              callback: (value: any, index: any, values: any) => xScalesCallback(value, values, locale, event, index, granularity, hideTimezone),
              position: 'right',
              font: {
                size: 12
              },
            },
            offset: false,
            beginAtZero: false,
            grid: {},
            border: {},
            title: {},
            id: "x",
            position: "bottom",
            ...(chartStartTime && chartEndTime ? { min: chartStartTime, max: chartEndTime } : {}),
          },
          y: {
            axis: "y",
            type: "linear",
            ticks: {
              callback: (value: any, index: any, ticks: any) => yScalesCallback(value, product, eventNode, ticks, locale),
              ...(yTickStepSize !== undefined ? { stepSize: yTickStepSize } : {}),
              font: {
                size: 12,
              },
            },
            beginAtZero: false,
            grid: {},
            border: {},
            title: {},
            id: "y",
            position: "left",
            afterDataLimits(scale: Scale) {
              const offset = (scale.max - scale.min) * 0.1; // 10% padding
              scale.max = scale.max + offset;
            }
          },
        },
      },
    });
}

